// Canonical list of food service job roles used everywhere a role is
// surfaced to the user (onboarding, shift posting, shift search,
// availability posting, etc.). All role pickers must read from here.
//
// Roles within each group are ordered by rank/seniority (most senior first).

export type RoleTuple = [key: string, label: string, icon: string];

export type RoleGroup = {
  group: string;
  roles: Array<RoleTuple>;
};

export type RoleMeta = { label: string; icon: string };

export type RoleOption = { label: string; value: string };

export const FOOD_SERVICE_ROLES: Array<RoleGroup> = [
  {
    group: "Front of House",
    roles: [
      ["waitstaff_trainer", "Waitstaff Trainer", "📚"],
      ["sommelier", "Sommelier", "🍇"],
      ["kitchen_expeditor", "Kitchen Expeditor", "🔔"],
      ["bartender", "Bartender", "🍸"],
      ["server", "Server / Waitstaff", "🍽️"],
      ["cocktail_server", "Cocktail Server", "🍹"],
      ["host_hostess", "Host / Hostess", "📋"],
      ["barista", "Barista", "☕"],
      ["customer_service_representative", "Customer Service Representative", "🎧"],
      ["cashier", "Cashier", "💳"],
      ["food_runner", "Food Runner", "🏃"],
      ["barback", "Barback", "🍷"],
      ["busser", "Busser", "🧹"],
      ["stagiaire_shadow_foh", "Stagiaire/shadow (front of house)", "🎓"],
    ],
  },
  {
    group: "Back of House",
    roles: [
      ["sous_chef", "Sous Chef", "🍳"],
      ["pastry_chef", "Pastry Chef", "🧁"],
      ["line_cook", "Line Cook", "🔥"],
      ["baker", "Baker", "🥖"],
      ["prep_cook", "Prep Cook", "🔪"],
      ["bakers_assistant", "Baker's Assistant", "🥐"],
      ["food_prep_worker", "Food Prep Worker", "🥕"],
      ["kitchen_assistant", "Kitchen Assistant", "🥄"],
      ["dishwasher", "Dishwasher", "🧼"],
      ["stagiaire_shadow_boh", "Stagiaire/shadow (back of house)", "🎓"],
    ],
  },
  {
    group: "Catering & Events",
    roles: [
      ["catering_chef", "Catering Chef", "🍲"],
      ["event_planner", "Event Planner", "🎉"],
      ["catering_coordinator", "Catering Coordinator", "📅"],
      ["banquet_server", "Banquet Server", "🍴"],
      ["catering_assistant", "Catering Assistant", "🧺"],
      ["event_set_up_crew", "Event Set-Up Crew", "🎪"],
      ["catering_delivery_driver", "Catering Delivery Driver", "🚐"],
    ],
  },
  {
    group: "Management",
    roles: [
      ["general_manager", "General Manager", "🗂️"],
      ["executive_chef", "Executive Chef", "👨‍🍳"],
      ["assistant_manager", "Assistant Manager", "📊"],
      ["shift_lead", "Shift Lead", "⭐"],
    ],
  },
  {
    group: "Support & Other",
    roles: [
      ["security_personnel", "Security Personnel", "🛡️"],
      ["delivery_driver", "Delivery Driver", "🚗"],
    ],
  },
];

export const ROLE_OPTIONS: Array<RoleOption> = FOOD_SERVICE_ROLES.flatMap((g) =>
  g.roles.map(([value, label]) => ({ label, value })),
);

export const ROLE_NAME: Record<string, RoleMeta> = Object.fromEntries(
  FOOD_SERVICE_ROLES.flatMap((g) =>
    g.roles.map(([key, label, icon]) => [key, { label, icon }]),
  ),
);
